// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { MIN_PASSWORD_LENGTH, useResetPassword } from '../../api/auth.js';
import { BrandSymbol, BrandWordmark } from '../../components/BrandMark.js';

/**
 * Complete a password reset using the token from the emailed link
 * (intakeform-preview-r5t): `${WEB_ORIGIN}/reset-password?token=…`.
 */
export function ResetPasswordRoute() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`Password must be at least ${String(MIN_PASSWORD_LENGTH)} characters.`);
      return;
    }
    if (newPassword !== confirm) {
      setLocalError('Passwords do not match.');
      return;
    }
    reset.mutate({ token, newPassword });
  };

  const error =
    localError ??
    (reset.error
      ? reset.error.message || 'Could not reset your password. The link may have expired.'
      : null);

  return (
    <main className="login-main" id="main-content">
      <div className="login-card">
        <div className="login-brand">
          <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
          <BrandWordmark className="brand-wordmark" />
        </div>
        <h1 className="login-title">Choose a new password</h1>
        {token === '' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="error-banner" role="alert">
              This reset link is missing its token. Request a new one.
            </div>
            <Link to="/forgot-password" className="text-sm">
              Request a reset link
            </Link>
          </div>
        ) : reset.isSuccess ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p
              role="status"
              aria-live="polite"
              className="text-sm font-medium"
              style={{ color: 'rgb(var(--color-primary))' }}
            >
              Your password has been reset.
            </p>
            <Link to="/login" data-variant="primary" style={{ width: '100%', textAlign: 'center' }}>
              Sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} aria-describedby={error ? 'reset-error' : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label htmlFor="reset-new">
                <span>New password</span>
                <input
                  id="reset-new"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  aria-required="true"
                  minLength={MIN_PASSWORD_LENGTH}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.currentTarget.value)}
                  style={{ width: '100%' }}
                />
              </label>
              <label htmlFor="reset-confirm">
                <span>Confirm new password</span>
                <input
                  id="reset-confirm"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  aria-required="true"
                  value={confirm}
                  onChange={(e) => setConfirm(e.currentTarget.value)}
                  style={{ width: '100%' }}
                />
              </label>
              {error ? (
                <div id="reset-error" className="error-banner" role="alert" aria-live="assertive">
                  {error}
                </div>
              ) : null}
              <button
                type="submit"
                data-variant="primary"
                disabled={reset.isPending}
                style={{ width: '100%' }}
              >
                {reset.isPending ? 'Resetting…' : 'Reset password'}
              </button>
              <Link to="/login" className="text-sm" style={{ textAlign: 'center' }}>
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
